import {SummaryStrip} from '../ui/SummaryStrip';
import {PageHeader} from '../ui/PageHeader';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import {useEffect,useState} from 'react';
import {useSearchParams} from 'react-router';
import {api} from '../api';
import {useStaffSession} from '../ui/StaffSession';
import {ErrorState,Loading} from '../ui/State';
import {DateOnlyInput} from '../components/DateOnlyInput';
import {repairPriorityLabels} from '../ui/work-queue-navigation';

// How repairs are going (owner brief, 5 Oct 2026; R6): repairs raised as tickets in a period, the share handled within their
// response, attendance and completion targets, repeat repairs, and where the work is by priority and trade.
type Kpis={period:{from:string;to:string};raised:number;open:number;toConfirm:number;awaitingPhotos:number;recalls:number;completed:number;
 respondedInTime:number|null;attendedInTime:number|null;completedInTime:number|null;medianDaysToSignOff:number|null;
 byPriority:{priority:string;raised:number;open:number;missed:number}[];byTrade:{trade:string;raised:number}[]};
const day=(offset:number)=>new Date(Date.now()-offset*86400_000).toISOString().slice(0,10);

export function RepairPerformance(){
 const session=useStaffSession(),building=session?.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;
 const [params,setParams]=useSearchParams(),from=params.get('from')??day(89),to=params.get('to')??day(0);
 const [data,setData]=useState<Kpis>(),[error,setError]=useState<unknown>();
 useEffect(()=>{let active=true;setError(undefined);const q=new URLSearchParams({from,to});if(building)q.set('building',building);
  void api<Kpis>(`/api/work/repair-kpis?${q}`).then(r=>{if(active)setData(r);}).catch(e=>{if(active)setError(e);});return()=>{active=false;};},[building,from,to]);
 const pct=(v:number|null)=>v===null?'—':`${v}%`;
 const maxTrade=Math.max(1,...(data?.byTrade.map(t=>t.raised)??[1]));
 return <>
  <PageHeader title="Repair performance" context="Repairs raised from emails, calls and messages, and how quickly they were handled against their targets."/>
  <form className="repair-kpi-period" onSubmit={e=>e.preventDefault()}>
   <label>From<DateOnlyInput name="from" value={from} onChange={v=>{if(v)setParams(p=>{p.set('from',v);return p;},{replace:true});}}/></label>
   <label>To<DateOnlyInput name="to" value={to} onChange={v=>{if(v)setParams(p=>{p.set('to',v);return p;},{replace:true});}}/></label>
  </form>
  {error?<ErrorState error={error}/>:!data?<Loading/>:<>
   <SummaryStrip mode="metric" ariaLabel="Repair workload in the selected period" items={[
    {label:'Raised',value:data.raised},{label:'Still open',value:data.open},{label:'To confirm',value:data.toConfirm},
    {label:'Waiting for photos',value:data.awaitingPhotos},{label:'Repeat repairs (90 days)',value:data.recalls},
   ]}/>
   <section className="panel repair-performance-targets" aria-label="Recorded repair target performance"><h2>Target performance</h2><dl>
    <div><dt>Responded within target</dt><dd>{pct(data.respondedInTime)}</dd></div>
    <div><dt>Attended within target</dt><dd>{pct(data.attendedInTime)}</dd></div>
    <div><dt>Completed within target</dt><dd>{pct(data.completedInTime)}</dd></div>
    <div><dt>Median days to sign-off</dt><dd>{data.medianDaysToSignOff??'—'}</dd></div>
   </dl><p className="form-help">A dash means no recorded result is available for this period.</p></section>
   <div className="repair-performance-breakdown">
   <section className="panel"><h2>By priority</h2>{!data.byPriority.length?<p>No repairs in this period.</p>:

<ResponsiveTable className="repair-kpi-table"><thead><tr><th>Priority</th><th>Raised</th><th>Open</th><th>Missed a target</th></tr></thead>
     <tbody>{data.byPriority.map(p=><tr key={p.priority}><td>{repairPriorityLabels[p.priority]??p.priority}</td><td>{p.raised}</td><td>{p.open}</td><td>{p.missed}</td></tr>)}</tbody></ResponsiveTable>
}</section>
   <section className="panel"><h2>By trade</h2>{!data.byTrade.length?<p>No repairs in this period.</p>:
    <ul className="repair-kpi-bars">{data.byTrade.map(t=><li key={t.trade}><span>{t.trade}</span><span className="bar" style={{width:`${Math.round(t.raised/maxTrade*100)}%`}} aria-hidden="true"/><strong>{t.raised}</strong></li>)}</ul>}</section>
   </div>
  </>}
 </>;
}
